/**
 * useScanSession — 스캔 화면의 카메라/엔진 세션 상태 머신.
 *
 * 상태:
 *   idle              비활성 (탭 비활성·정지 후)
 *   starting          권한 → lease → startScan 진행 중
 *   active            네이티브 세션 동작 중 (android/ios 모두 "card" 엔진 — 2026-08-20 iOS 이식)
 *   model-pending     asset-delivery lease 실패 — "모델 준비 중" (syncAssets 트리거됨)
 *   permission-denied 카메라 권한 거부
 *   engine-error      startScan/엔진 실패 또는 서킷 브레이커 — 자동 재시작 금지, retry() 로만
 *   dev-fake          비운영 빌드 한정 폴백 — 가짜 emitter 로 오버레이 좌표계 확인
 *
 * 핵심 규약:
 *  - ScanPage 는 main-tab 슬롯 keep-alive 라 탭 전환에도 unmount 되지 않는다.
 *    시작/정지는 반드시 useMainTabActive() 전이(active prop)에 묶는다.
 *  - 시작 시퀀스는 비동기 — 세대(generation) 가드로 낡은 완료를 self-cancel 하고
 *    single-flight 로 직렬화한다 (늦은 Promise 가 숨은 탭에서 카메라를 켜는 것 방지).
 *  - lease 3종은 acquireCardModelLeases 가 원자적으로 다룬다: 부분 실패 시 기획득분
 *    release, releaseId 불일치(sync 가 사이에 낀 세대 혼합) 시 전량 반환 후 1회 재시도.
 *  - interrupted 는 reason 별 분기: background → 복귀 시 재시작(App 'resume' 이벤트),
 *    engine 등 → engine-error (고장난 엔진 무한 재기동 금지).
 *  - html[data-scan-live] 는 active 상태에서만 설정 — 웹뷰 투명화 CSS 스코프.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getPlatform, isNative, nativeWarn } from "../../native";
import {
  LiveVision,
  QuadOverlay,
  startScanSession,
  type CaptureAndCropResult,
  type DetectionEvent,
  type ScanSessionHandle,
  type ScanStateEvent,
} from "../../native/liveVision";
import {
  acquireAsset,
  releaseAssetLease,
  syncAssets,
  type AssetLease,
} from "../../native/assetDelivery";
import { unwrapAssetKeys } from "../../api/asset";
import { useScanLangStore } from "../../store/scanLangStore";
import { computeCardGuide } from "./scanGuide";

export type ScanSessionState =
  | "idle"
  | "starting"
  | "active"
  | "model-pending"
  | "permission-denied"
  | "engine-error"
  | "dev-fake";

/** card 엔진 kind 3종 — 엔진 engineOptions 및 어드민 릴리스 매핑과 1:1 계약 */
const CARD_MODEL_KINDS = ["card-seg", "card-verifier", "card-config"] as const;

/**
 * still 정밀화 seg 모델 (detector=hybrid 번들 전용) — **선택적** kind.
 * 필수 3종과 달리 비하이브리드 릴리스에는 파일이 없으므로 실패해도 스캔을 막지 않는다.
 * 엔진 쪽 계약: config 가 hybrid 를 선언했는데 이 경로가 없으면 세션 시작이 fail-fast.
 */
const CARD_REFINE_KIND = "card-refine";

/**
 * crop 코너 정밀화 모델 (B단계, 2026-08-24) — **선택적** kind. config 가 `card_corner`
 * 를 선언한 신형 하이브리드 번들 전용으로, still·라이브 코너 정밀화에 쓰인다.
 * 엔진 쪽 계약: config 가 card_corner 를 선언했는데 이 경로가 없으면 세션 시작 fail-fast.
 */
const CARD_CORNER_KIND = "card-corner";

/** 온디바이스 매칭 kind 5종 — CardEngine.matchCard 계약과 1:1 */
export const CARD_MATCHER_KINDS = [
  "card-embed",
  "card-embed-data",
  "card-index",
  "card-serlno",
  "card-matcher-config",
] as const;

/**
 * 인덱스 사이드카(카드명·번호·세트코드) — **선택** kind. 실려 있으면 엔진이 매치 결과에
 * 이 값들을 붙여 보내고, FE 는 OCR 교차검증을 카탈로그 API 왕복 없이 끝낸다.
 * 없는 릴리스(2026-08-25 이전 전부)에서는 종전대로 서버 조회로 메운다.
 */
export const CARD_MATCHER_META_KIND = "card-meta";

export async function releaseLeases(leases: AssetLease[]): Promise<void> {
  await Promise.all(leases.map((lease) => releaseAssetLease(lease.leaseId)));
}

const releaseAll = releaseLeases;

/**
 * lease 세트 획득 — 부분 실패 시 기획득분 release, releaseId 불일치 시 전량 반환 후
 * 1회 재시도. 미반환 lease 는 해당 세대가 디스크 정리 대상에서 제외되는 누수라
 * 어떤 실패 경로에서도 남기지 않는다. 반환 순서 = kinds 순서.
 */
async function acquireLeaseSet(kinds: readonly string[]): Promise<AssetLease[]> {
  const attempt = async (): Promise<AssetLease[]> => {
    const acquired: AssetLease[] = [];
    try {
      for (const kind of kinds) {
        acquired.push(await acquireAsset(kind));
      }
      return acquired;
    } catch (err) {
      await releaseAll(acquired);
      throw err;
    }
  };

  let leases = await attempt();
  if (new Set(leases.map((l) => l.releaseId)).size > 1) {
    // sync 가 획득 사이에 끼면 세대 혼합 가능 — 모델과 threshold/인덱스는 한 세트여야 한다
    await releaseAll(leases);
    leases = await attempt();
    if (new Set(leases.map((l) => l.releaseId)).size > 1) {
      await releaseAll(leases);
      throw Object.assign(new Error("asset leases span multiple releases"), {
        code: "RELEASE_MISMATCH",
      });
    }
  }
  return leases;
}

export async function acquireCardModelLeases(): Promise<AssetLease[]> {
  return acquireLeaseSet(CARD_MODEL_KINDS);
}

/**
 * ⚠ variant 를 지정하지 않는다. manifest 가 kind 당 하나만 내려주므로(CONTRACT.md §0)
 * 보유한 그것이 답이고, 콕 집으면 받지 않은 대안을 요구해 NOT_READY 가 된다.
 * 인식 언어는 lease 가 아니라 **sync 단**에서 정해진다 — configureAssetDelivery 의
 * variants 로 넘어가 서버가 그 언어 인덱스만 내리고, 언어를 바꾸면 재-sync 한다.
 * (lease 로 고르려면 언어팩을 전부 받아야 해서 크기 이득이 사라진다.)
 */
export async function acquireCardMatcherLeases(): Promise<AssetLease[]> {
  const leases = await acquireLeaseSet(CARD_MATCHER_KINDS);
  // 사이드카는 선택이라 별도로 잡는다 — 필수 5종에 섞으면 사이드카 없는 릴리스에서
  // 매칭 자체가 죽는다. 세대가 다르면 버린다: 사이드카는 인덱스 **행 순서**에 붙는
  // 파일이라 세대가 어긋나면 엉뚱한 카드 이름이 되고, 그건 조용한 오답이다.
  try {
    const meta = await acquireAsset(CARD_MATCHER_META_KIND);
    if (meta.releaseId === leases[0].releaseId) return [...leases, meta];
    await releaseAssetLease(meta.leaseId).catch(() => undefined);
  } catch {
    // 사이드카 없는 릴리스 — 정상 경로
  }
  return leases;
}

/** 가짜 outline emitter — 카드 하나가 화면 안에서 천천히 떠다니는 궤적 (dev 폴백 전용). */
export function makeFakeDetection(sessionId: string, frameId: number, now: number): DetectionEvent {
  const cx = 0.5 + 0.13 * Math.sin(now / 2300);
  const cy = 0.46 + 0.09 * Math.sin(now / 3100 + 1.3);
  const halfW = 0.155 + 0.012 * Math.sin(now / 1700);
  // 카드 비율 63:88 — 정규화 좌표는 프레임(720×1280) 기준이므로 종횡비 보정
  const halfH = halfW * (88 / 63) * (720 / 1280);
  const ang = 0.06 * Math.sin(now / 2700);
  const cos = Math.cos(ang);
  const sin = Math.sin(ang);
  const rot = (dx: number, dy: number): { x: number; y: number } => ({
    x: cx + dx * cos - dy * sin,
    y: cy + dx * sin + dy * cos,
  });
  return {
    sessionId,
    frameId,
    timestampMs: now,
    detected: true,
    quads: [{
      corners: [rot(-halfW, -halfH), rot(halfW, -halfH), rot(halfW, halfH), rot(-halfW, halfH)],
      score: 0.93,
    }],
    frameW: 720,
    frameH: 1280,
    engine: "fake-emitter",
    elapsedMs: 1,
  };
}

interface UseScanSessionOptions {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /** useMainTabActive() — keep-alive 슬롯의 활성 여부. false 전이에서 반드시 정지한다. */
  active: boolean;
  /**
   * 세션 시작 실패 시 가짜 emitter 폴백 허용 여부.
   * 기본: 비운영 빌드(MODE !== 'prod')에서만 — 운영에서 가짜 검출로 장애를 은폐하지 않는다.
   */
  allowFakeFallback?: boolean;
  /**
   * 검출 이벤트 콜백 (표시방향 정규화 좌표). 지정 시 오버레이의 검출 quad morph 를
   * 끄고(가이드 고정) 시각화를 호출부에 맡긴다 — 자동 셔터/점 표시용.
   */
  onDetection?: (ev: DetectionEvent) => void;
  /**
   * 라이브 힌트 매칭용 matcher lease 5종 공급자 (kinds = CARD_MATCHER_KINDS 순서).
   * lease 소유권은 공급자(호출부 캐시)에 있다 — 여기서는 경로만 engineOptions 로
   * 전달하고 반환하지 않는다. null 이면(미발행·다운로드 전) 힌트 없이 진행 — 비필수.
   */
  getMatcherLeases?: () => Promise<AssetLease[] | null>;
}

export function useScanSession({
  canvasRef,
  active,
  allowFakeFallback,
  onDetection,
  getMatcherLeases,
}: UseScanSessionOptions) {
  const allowFake = allowFakeFallback ?? import.meta.env.MODE !== "prod";
  const [state, setState] = useState<ScanSessionState>("idle");
  /**
   * 현재 세션에 라이브 힌트용 matcher 경로가 실제로 붙었는가.
   * matcher 는 **비필수**라 없으면 model-pending 도 안 되고 그냥 active 로 간다 — 그래서
   * 호출부가 이걸 못 보면 "힌트 없는 세션"이 조용히 영구화된다(엔진은 matcherPaths 가
   * null 이면 힌트를 아예 안 돈다). 경로는 startScan 인자라 **세션 재시작으로만** 바뀐다.
   */
  const [matcherAttached, setMatcherAttached] = useState(false);

  // 최신 콜백 참조 — start() 의 세션 클로저가 재시작 없이 항상 현재 콜백을 쓰게
  const onDetectionRef = useRef(onDetection);
  onDetectionRef.current = onDetection;
  const getMatcherLeasesRef = useRef(getMatcherLeases);
  getMatcherLeasesRef.current = getMatcherLeases;

  // 세대 가드 — 모든 정지/재시작이 증가시키고, 비동기 완료는 자기 세대가 최신일 때만 반영
  const generationRef = useRef(0);
  // 시작 직렬화 체인 — boolean single-flight 는 StrictMode(dev)의 mount→cleanup→mount
  // 재진입에서 시작 요청이 통째로 증발한다: A 진행 중 → stop 이 세대 증가 → B 는
  // "진행 중" 이라 early-return → A 는 stale 로 자멸 → 아무도 세션을 못 연다
  // (에뮬레이터 실확인 — 스캔 화면이 영구 암전). 뒤 요청은 앞 시도 완료 후 이어서 실행.
  const startingRef = useRef<Promise<void> | null>(null);
  const sessionRef = useRef<ScanSessionHandle | null>(null);
  const leasesRef = useRef<AssetLease[]>([]);
  const fakeRef = useRef<{ overlay: QuadOverlay; timer: ReturnType<typeof setInterval> } | null>(null);
  const resumePendingRef = useRef(false);
  // resume 리스너(등록 1회)가 최신 값을 보게 하는 늦은 바인딩
  const activeRef = useRef(active);
  activeRef.current = active;
  const startRef = useRef<() => Promise<void>>(async () => {});
  const captureBusyRef = useRef(false);
  /**
   * 촬영 busy 의 소유권 토큰. 세션 teardown 중에 진행 중이던 captureAndCrop 은
   * 네이티브 콜백이 유실돼 **영영 안 끝날 수 있다** (Android: stop 이 stillExecutor 를
   * shutdown 한 뒤 CameraX 취소 콜백이 배달되지 못함). boolean 단독이면 그 경우
   * finally 가 안 돌아 busy 가 영구 true — 이후 수동 셔터·OCR 스틸이 전부 무반응이다.
   * teardown 이 토큰을 올리며 즉시 잠금을 풀고, 낡은 촬영의 finally 는 토큰 불일치로
   * 남의 잠금을 건드리지 않는다.
   */
  const captureTokenRef = useRef(0);

  const releaseCaptureLock = () => {
    captureTokenRef.current++;
    captureBusyRef.current = false;
  };

  const clearScanLive = () => {
    delete document.documentElement.dataset.scanLive;
  };
  /**
   * 재시작(restart) 중에는 웹뷰 투명화를 **유지한다** (2026-09-07 제보: "다 받아져서
   * 재구동할 때 깜빡임").
   *
   * stop→start 사이에 `data-scan-live` 를 걷으면 웹뷰가 곧바로 불투명해져 페이지
   * 배경이 한 프레임 번쩍이고, 0.5초쯤 뒤 다시 투명해진다. 깜빡임의 정체는 카메라가
   * 아니라 **이 속성의 왕복**이다 — 카메라 프리뷰가 잠깐 없는 동안은 검은 화면이라
   * 그 위에 덮인 준비 중 오버레이와 구분되지 않는다.
   */
  const keepScanLiveRef = useRef(false);

  /** 세션·lease·fake·투명화 속성을 전부 회수. idempotent. */
  const stop = useCallback(async () => {
    generationRef.current++;
    resumePendingRef.current = false;
    releaseCaptureLock(); // 진행 중 촬영은 세션과 함께 무효 — busy 고착 방지
    const session = sessionRef.current;
    sessionRef.current = null;
    const leases = leasesRef.current;
    leasesRef.current = [];
    const fake = fakeRef.current;
    fakeRef.current = null;

    if (fake) {
      clearInterval(fake.timer);
      fake.overlay.stop();
    }
    if (session) await session.stop().catch((err) => nativeWarn("scan:stop", err));
    await releaseAll(leases).catch((err) => nativeWarn("scan:releaseLease", err));
    if (!keepScanLiveRef.current) clearScanLive();
    setMatcherAttached(false);
    setState("idle");
  }, []);

  // 가이드 프레임 — 화면 기준 카드 비율 박스 (computeCardGuide 는 스케일 불변이라
  // backing-store 픽셀 대신 CSS 크기로 충분; 레이아웃 전이면 뷰포트로 폴백)
  const guideFor = (canvas: HTMLCanvasElement) =>
    computeCardGuide(
      canvas.clientWidth || window.innerWidth,
      canvas.clientHeight || window.innerHeight,
    );

  const startFake = useCallback((generation: number) => {
    const canvas = canvasRef.current;
    if (!canvas || generationRef.current !== generation) return;
    const overlay = new QuadOverlay(canvas, { guide: guideFor(canvas) });
    overlay.setSession("fake-session");
    overlay.start();
    let frameId = 0;
    // 검출 fps(≈12)와 표시 fps(60, QuadOverlay rAF)는 분리 — 실 엔진과 동일 조건
    const timer = setInterval(() => {
      overlay.update(makeFakeDetection("fake-session", frameId++, performance.now()));
    }, 80);
    fakeRef.current = { overlay, timer };
    setState("dev-fake");
  }, [canvasRef]);

  const handleInterrupted = useCallback(
    (generation: number, reason: ScanStateEvent["reason"]) => {
      if (generationRef.current !== generation) return; // 낡은 세션의 이벤트
      void (async () => {
        releaseCaptureLock(); // stop() 과 동일 — interrupted 로 내려가도 busy 를 남기지 않는다
        const session = sessionRef.current;
        sessionRef.current = null;
        const leases = leasesRef.current;
        leasesRef.current = [];
        if (session) await session.stop().catch((err) => nativeWarn("scan:stopAfterInterrupt", err));
        await releaseAll(leases).catch((err) => nativeWarn("scan:releaseLease", err));
        clearScanLive();
        setMatcherAttached(false);
        if (reason === "background") {
          // 복귀 시 App 'resume' + 탭 활성 조건으로 재시작 (아래 resume 리스너 주석)
          resumePendingRef.current = true;
          setState("idle");
        } else {
          // engine(서킷 브레이커)·camera-lost — 자동 재시작 금지, retry()/탭 재진입만
          setState("engine-error");
        }
      })();
    },
    [],
  );

  const doStart = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (sessionRef.current || fakeRef.current) return; // 이미 동작 중 — 재진입 no-op

    const generation = ++generationRef.current;
    const stale = () => generationRef.current !== generation;
    setState("starting");

    try {
      // 이미 granted 면 requestPermissions 를 부르지 않는다 — Capacitor 기본
      // requestPermissions 는 granted 상태에서 launcher 콜백이 오지 않아 영구
      // 대기하는 문제가 있다 (에뮬레이터 실확인). 미허용일 때만 다이얼로그 요청.
      let permission = await LiveVision.checkPermissions().catch(() => null);
      if (stale()) return;
      if (permission && permission.camera !== "granted") {
        permission = await LiveVision.requestPermissions().catch(() => null);
        if (stale()) return;
      }
      if (permission && permission.camera !== "granted") {
        setState("permission-denied");
        return;
      }

      let engineOptions: Record<string, unknown> | undefined;
      if (isNative()) {
        let leases: AssetLease[];
        try {
          leases = await acquireCardModelLeases();
        } catch (err) {
          nativeWarn("scan:acquireLeases", err);
          if (!stale()) {
            setState("model-pending");
            void syncAssets().catch(() => undefined); // 재진입 안전 — 다음 시도에서 재확인
          }
          return;
        }
        if (stale()) {
          await releaseAll(leases).catch(() => undefined);
          return;
        }
        // 정밀화 모델은 선택적 — 세대 혼합이면 버린다 (필수 3종과 다른 releaseId 의
        // 정밀화 모델을 쓰면 모델 조합이 깨진다). refine=구형 seg / corner=신형 코너 회귀.
        const acquireOptional = async (kind: string): Promise<AssetLease | null> => {
          try {
            const lease = await acquireAsset(kind);
            if (lease.releaseId === leases[0].releaseId) return lease;
            await releaseAssetLease(lease.leaseId).catch(() => undefined);
          } catch {
            // 파일 없는 릴리스 — 정상 경로
          }
          return null;
        };
        const refine = await acquireOptional(CARD_REFINE_KIND);
        const corner = await acquireOptional(CARD_CORNER_KIND);
        const optional = [refine, corner].filter((l): l is AssetLease => l !== null);
        if (stale()) {
          await releaseAll([...leases, ...optional]).catch(() => undefined);
          return;
        }
        leasesRef.current = [...leases, ...optional];
        const [seg, verifier, config] = leases;

        // 보호 에셋(asset-delivery v2, DFC1) — protection 있는 lease 만 be 에서 DEK 언랩.
        // 실패하면 어차피 엔진이 복호화 못 하므로 여기서 throw → 아래 catch 가 lease 정리.
        const protectedLeases = leasesRef.current.filter(
          (lease) => (lease.protection ?? "NONE") !== "NONE",
        );
        const deks =
          protectedLeases.length > 0
            ? await unwrapAssetKeys(protectedLeases.map((lease) => lease.hash))
            : new Map<string, string>();
        if (stale()) {
          await releaseAll(leasesRef.current).catch(() => undefined);
          leasesRef.current = [];
          return;
        }
        const dekOf = (lease: AssetLease | null): string | undefined =>
          lease && (lease.protection ?? "NONE") !== "NONE" ? deks.get(lease.hash) : undefined;

        // 라이브 힌트 매칭용 matcher lease — **비필수**. 실패/미보유면 힌트 없이 진행.
        // lease 소유권은 공급자 캐시(ScanPage) — 세션보다 오래 살아 경로가 유효하다.
        // ⚠ 세션 중 언어 전환으로 공급자 캐시가 갈리면 이 세션의 힌트는 구 인덱스
        // 기준이다 — 엔진이 매칭 실패 시 세션 잔여 기간 힌트를 스스로 끈다.
        let matcher: AssetLease[] | null = null;
        if (getMatcherLeasesRef.current) {
          matcher = await getMatcherLeasesRef.current().catch(() => null);
          if (stale()) {
            await releaseAll(leasesRef.current).catch(() => undefined);
            leasesRef.current = [];
            return;
          }
        }

        engineOptions = {
          segModelPath: seg.path,
          // 라이브 힌트 OCR 언어 — 언어 전환은 세션 재시작을 유발하므로 시작 시점 값이 유효
          ocrLang: useScanLangStore.getState().lang,
          verifierModelPath: verifier.path,
          configPath: config.path,
          ...(refine ? { refineModelPath: refine.path } : {}),
          ...(corner ? { cornerModelPath: corner.path } : {}),
          ...(dekOf(seg) ? { segModelDek: dekOf(seg) } : {}),
          ...(dekOf(verifier) ? { verifierModelDek: dekOf(verifier) } : {}),
          ...(dekOf(config) ? { configDek: dekOf(config) } : {}),
          ...(dekOf(refine) ? { refineModelDek: dekOf(refine) } : {}),
          ...(dekOf(corner) ? { cornerModelDek: dekOf(corner) } : {}),
          ...(matcher && matcher.length >= CARD_MATCHER_KINDS.length
            ? {
                matcherEmbedPath: matcher[0].path,
                matcherEmbedDataPath: matcher[1].path,
                matcherIndexPath: matcher[2].path,
                matcherSerlnoPath: matcher[3].path,
                matcherConfigPath: matcher[4].path,
                // 6번째는 선택 사이드카 (acquireCardMatcherLeases 가 있을 때만 붙인다)
                ...(matcher[5] ? { matcherMetaPath: matcher[5].path } : {}),
              }
            : {}),
        };
      }

      // engineOptions 에 matcher 5경로가 다 실렸는지 — 라이브 힌트 가동 여부와 1:1
      const matcherAttachedNow =
        engineOptions !== undefined && engineOptions.matcherIndexPath !== undefined;
      // iOS 도 card 엔진 (2026-08-20 이식) — 내장 rect 는 더 이상 쓰지 않는다
      const engine = "card";
      const session = await startScanSession({
        canvas,
        // iOS 는 세션 프리셋이 분석·스틸을 한 해상도로 묶어 스틸이 720p 로 나온다 —
        // 컬렉터 번호가 판독 한계 아래라 스틸 확정의 이점이 통째로 사라진다.
        // (Android 플러그인은 이 옵션을 무시한다 — 원래 별도 1920×1080 스트림이다)
        //
        // 상한을 3840 으로 잡은 근거 두 가지:
        //  ① **iPhone 이 16:9 로 그 아래를 안 준다** — 1920 으로 잡았더니 맞는 포맷이
        //     없어 선택이 실패했다(실기기 2026-08-26).
        //  ② **4096 미만이면 OCR 이 원본 그대로 읽는다** — RgbImage.fromPhotoFile 의
        //     서브샘플이 2의 거듭제곱이라 긴 변 4096 부터 절반으로 깎인다. 실제로
        //     40MP(8448) 로 찍혔을 때는 2112 로 깎여 비용만 크고 얻는 게 없었다.
        //     3840 은 깎이지 않고 전부 OCR 에 도달하는 최대치다.
        scan: {
          engine,
          engineOptions,
          // **iOS 전용** — 스캔 모드가 사진을 찍지 않고 이 버퍼를 그대로 판정에
          // 쓰므로(ScanPage PHOTO_STILL 주석) 분석 해상도가 곧 판정·OCR 해상도다.
          // 720p 면 컬렉터 번호가 8px 로 판독 한계 아래. 엔진 힌트 승격 상한(2048)
          // 때문에 1920 이 낭비 없는 최대다.
          // ⚠ Android 는 명시적으로 뺀다 — 판정은 별도 ImageCapture(1920×1080)가
          // 하므로 라이브를 올리면 검출·변환·발열만 비싸진다. 실제로 1080p 통일을
          // 시도했다가 폴드7 브래킷 회귀로 하루 만에 되돌렸다(2026-08-26).
          ...(getPlatform() === "ios" ? { analysisSize: { w: 1920, h: 1080 } } : {}),
        },
        guide: guideFor(canvas),
        // onDetection 소비자가 있으면 오버레이 morph 를 끈다 — 가이드 고정 + 점 표시
        updateOverlay: onDetectionRef.current === undefined,
        onDetection: (ev) => onDetectionRef.current?.(ev),
        onInterrupted: (reason) => handleInterrupted(generation, reason),
      });
      if (stale()) {
        // 늦은 완료 — 숨은 탭에서 카메라가 켜지지 않도록 즉시 회수
        await session.stop().catch(() => undefined);
        return;
      }
      sessionRef.current = session;
      document.documentElement.dataset.scanLive = "true";
      setMatcherAttached(matcherAttachedNow);
      setState("active");
    } catch (err) {
      nativeWarn("scan:start", err);
      if (stale()) return;
      const leases = leasesRef.current;
      leasesRef.current = [];
      await releaseAll(leases).catch(() => undefined);
      if (allowFake) startFake(generation);
      else setState("engine-error");
    }
  }, [allowFake, canvasRef, handleInterrupted, startFake]);

  const start = useCallback(async () => {
    if (!isNative()) return; // 웹은 라우트에서 차단 — 방어적 이중 가드
    const prev = startingRef.current;
    const run = (async () => {
      if (prev) await prev.catch(() => undefined);
      await doStart();
    })();
    startingRef.current = run;
    try {
      await run;
    } finally {
      if (startingRef.current === run) startingRef.current = null;
    }
  }, [doStart]);
  startRef.current = start;

  // 탭 활성 전이 = 유일한 시작/정지 지점 (keep-alive 라 unmount cleanup 에 의존 불가)
  useEffect(() => {
    if (active) void start();
    else void stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // 실제 unmount(라우터 이탈 등 희귀 경로) 대비 최종 회수
  useEffect(() => {
    return () => {
      void stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * interrupted 복귀 재시작 — Capacitor App `resume` 이벤트를 직접 듣는다.
   *
   * ⚠ **`appStateChange`(useAppResume)로는 못 잡는 구멍이 있다** (2026-09-10 실기기).
   *   Capacitor 는 `isActive=false` 를 **onStop** 에서만 쏘는데(BridgeActivity.onStop,
   *   activityDepth 0), live-vision 플러그인은 **onPause** 에서 세션을 끊는다
   *   (LiveVisionPlugin.handleOnPause → interruptSession("background")).
   *   그래서 **멈추기만 하고 정지하지는 않는** 방해 — 위에 뜨는 시스템 다이얼로그,
   *   권한 창, 반투명 액티비티 — 가 끼면 이렇게 된다:
   *     onPause → 카메라 죽음 + interrupted 수신 → (isActive=false 는 안 옴)
   *     → onResume → useAppResume 은 "백그라운드로 간 적이 없다"며 콜백을 삼킴
   *     → resumePending 이 영영 남고 카메라는 안 돌아온다.
   *   증상은 "브래킷도 카메라도 안 나오는 검은 화면"이고, 화면을 나갔다 들어와야만
   *   낫는다(그때는 mount 가 새 세션을 연다). 실제 발화 경로는 로그인 중에 뜨는
   *   16KB 정렬 경고창이었다 — [[peeka-16kb-page-alignment]].
   *   `resume` 은 onResume 에서 나오므로 **정지까지 간 경우와 멈추기만 한 경우를 모두**
   *   덮는다(AppPlugin.handleOnResume, hasPausedEver 가드).
   * ⚠ 체류 시간 게이트는 두지 않는다 — 한순간 멈췄다 돌아와도 네이티브 세션은 이미
   *   죽어 있다. 재시작 여부는 오직 resumePendingRef 가 정한다.
   */
  useEffect(() => {
    if (!isNative()) return;
    let cancelled = false;
    let remove: (() => void) | null = null;
    void (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("resume", () => {
          if (!activeRef.current || !resumePendingRef.current) return;
          resumePendingRef.current = false;
          void startRef.current();
        });
        if (cancelled) {
          void handle.remove();
          return;
        }
        remove = () => void handle.remove();
      } catch (err) {
        nativeWarn("scan:resumeListener", err);
      }
    })();
    return () => {
      cancelled = true;
      remove?.();
    };
  }, []);

  /** 수동 재시도 (engine-error / model-pending UI 버튼) — 세션이 없을 때만 의미가 있다 */
  const retry = useCallback(() => {
    if (active) void start();
  }, [active, start]);

  /**
   * **동작 중인 세션을 다시 연다** (2026-08-18).
   *
   * ⚠ retry()/start() 로는 안 된다 — doStart 가 `sessionRef.current` 가 있으면 재진입
   * no-op 으로 즉시 빠져나가기 때문이다. 그래서 active 상태에서의 재시작 요청은 지금까지
   * **전부 조용히 무시됐다**. 실제 증상: 모델을 받은 뒤 앱을 재시작하지 않으면 인식이
   * 영영 안 됐고(matcher 경로가 안 붙은 세션이 고착), 언어를 바꿔도 이전 언어 인덱스로
   * 힌트가 계속 돌았다. 둘 다 "세션 재시작으로만 반영된다"고 적어두고 정작 재시작이
   * 일어나지 않던 것이다.
   *
   * engineOptions(모델·matcher 경로)는 startScan 인자라 **stop → start 만이 유일한 경로**다.
   */
  const restart = useCallback(async () => {
    if (!active) return;
    keepScanLiveRef.current = true; // 투명화 왕복 = 깜빡임 (keepScanLiveRef 주석)
    try {
      await stop();
      await start();
    } finally {
      keepScanLiveRef.current = false;
      // 다시 열리지 못했으면(권한·엔진 오류) 투명화를 걷는다 — 안 걷으면 오류
      // 오버레이 뒤로 아무것도 없는 투명한 화면이 남는다
      if (sessionRef.current === null) clearScanLive();
    }
  }, [active, stop, start]);

  /**
   * 정지 캡처 — active 상태에서만, 진행 중 재탭은 무시(busy 가드).
   * detected:false 처리·스트립 관리·deleteCrop 은 호출부(ScanPage) 책임.
   */
  const capture = useCallback(async (
    opts?: { quality?: "max" },
  ): Promise<CaptureAndCropResult | null> => {
    if (captureBusyRef.current || sessionRef.current === null) return null;
    captureBusyRef.current = true;
    const token = ++captureTokenRef.current;
    try {
      // returnFull: 전체 스틸도 저장 — 촬영 정지+수동 코너 조정 UI 가 표시/매칭에 사용
      // quality:"max" = 고해상 에스컬레이션 1회 촬영 (셸 takeStillMax — 자주 금지)
      return await LiveVision.captureAndCrop({
        outWidth: 630,
        outHeight: 880,
        returnCrop: "path",
        returnFull: true,
        ...(opts?.quality ? { quality: opts.quality } : {}),
      });
    } catch (err) {
      nativeWarn("scan:capture", err);
      return null;
    } finally {
      // teardown 이 토큰을 올렸으면 이 잠금은 이미 남의 것 — 건드리지 않는다
      if (captureTokenRef.current === token) captureBusyRef.current = false;
    }
  }, []);

  // stop: 촬영 정지(편집) 진입 시 라이브 세션을 명시적으로 내리는 용도 — idempotent
  return { state, matcherAttached, retry, restart, capture, stop };
}
